import type { Control } from "../core/Control.js";
import { alignAxis, type Alignment, type Size } from "../../common/geometry.js";
import { Panel, type PanelOptions } from "./Panel.js";
import type { Orientation } from "./ScrollBar.js";

/**
 * How a child sits across the panel's main axis.
 *
 * An alias rather than a second list: a control aligned in a Grid cell and a control aligned in
 * a plain container are saying the same word, and two spellings of it would drift.
 */
export type CrossAlign = Alignment;

export interface LayoutPanelOptions extends PanelOptions {
  /** The axis children are laid out along. */
  orientation?: Orientation;
  /** Gap between children along the main axis. */
  spacing?: number;
  /** Placement across the main axis. */
  align?: CrossAlign;
  /** Resize the panel to exactly fit its content. */
  autoSize?: boolean;
}

/**
 * Shared machinery for panels that position their own children.
 *
 * A child's `margin` is space these panels hold clear around it, on both axes: along the
 * stacking axis it pushes the next child along, and across it the child is placed inside what
 * the margin leaves. That is WPF's rule, and without it a picture with a margin and a paragraph
 * after it end up on top of each other.
 *
 * A child's size is whatever it was given, so these panels arrange using each child's *current*
 * size rather than a computed one. That matches the rest of the framework, which is
 * WinForms-shaped: the panel decides where children go, not how big they are, except where told
 * to stretch or to use a uniform cell.
 *
 * A child that sets `autoWidth` or `autoHeight` opts out of that on the axis it names: the panel
 * asks it for a `preferredSize` on every pass instead of reading back a size. That is the whole
 * measure pass — narrow, per-axis and off by default — and it is what lets a layout be written
 * without a single number in it.
 *
 * Layout re-runs when the panel resizes, when children are added or removed,
 * when a child resizes or is hidden, and after a theme change.
 */
export abstract class LayoutPanel extends Panel {
  orientation: Orientation = "vertical";
  spacing = 0;
  align: CrossAlign = "stretch";
  autoSize = false;

  /** Stops a layout-driven child resize from starting another layout. */
  #arranging = false;
  #watched = new Map<Control, () => void>();
  /**
   * The size each child would take if the panel were not stretching it.
   *
   * Stretching writes back to the child's real bounds, so without this the
   * next pass would read the stretched size as if the child had asked for it
   * and the layout would ratchet — a filled child never shrinking again, a
   * wrap line never getting shorter.
   */
  #natural = new WeakMap<Control, { width: number; height: number }>();

  constructor(options: LayoutPanelOptions = {}) {
    super(options);
    this.orientation = options.orientation ?? "vertical";
    this.spacing = options.spacing ?? 0;
    this.align = options.align ?? "stretch";
    this.autoSize = options.autoSize ?? false;
    // No layout here: a concrete subclass still has its own options to read.
    // Each one calls performLayout() at the end of its own constructor.
  }

  get horizontal(): boolean {
    return this.orientation === "horizontal";
  }

  /**
   * Position the children. `available` is the client size, already inside the
   * padding. Returns the extent the children ended up occupying.
   */
  protected abstract arrange(available: Size): Size;

  /**
   * The extent the children would occupy in `available`, writing nothing.
   *
   * The same reasoning as `arrange` without the `setBounds` calls, so a panel can be asked how
   * big it wants to be by a parent that has not decided yet — which is what happens whenever one
   * of these panels is itself an auto-sized child.
   */
  protected abstract measure(available: Size): Size;

  /** Resize the panel to fit `content`; subclasses pick which axis grows. */
  protected abstract applyAutoSize(content: Size): void;

  override preferredSize(available: Size): Size {
    const pad = this.padding;
    const inner = {
      width: Math.max(0, available.width - pad.left - pad.right),
      height: Math.max(0, available.height - pad.top - pad.bottom),
    };
    const content = this.measure(inner);
    return {
      width: content.width + pad.left + pad.right,
      height: content.height + pad.top + pad.bottom,
    };
  }

  /**
   * A child re-measured: re-arrange, without disturbing the container-level passes.
   *
   * What this panel would ask for has changed with it, so a parent measuring this panel is told
   * too. Without that the news stopped one level up: a paragraph that grew inside a stack inside
   * a grid re-arranged the stack and left the grid holding the stack's old height.
   */
  override onChildMeasureChanged(): void {
    this.performLayout();
    this.invalidateMeasure();
  }

  protected override performLayout(): void {
    // Container's constructor can reach this through onChildrenChanged before
    // the fields above exist, so `orientation` doubles as a "constructed
    // enough to lay out" sentinel.
    if (this.orientation === undefined || this.#arranging) return;
    this.#arranging = true;
    try {
      this.#watch();
      const content = this.arrange(this.clientSize);
      if (this.autoSize) this.applyAutoSize(content);
    } finally {
      this.#arranging = false;
    }
  }

  /** Children the panel positions: everything currently visible. */
  protected laidOutChildren(): Control[] {
    return this.controls.toArray().filter((c) => c.visible);
  }

  /**
   * The child's own size, ignoring any stretching this panel has applied.
   * Always use this when measuring, never `child.width` / `child.height`.
   *
   * An axis the child measures for itself comes from `preferredSize` instead. The recorded
   * natural size exists to stop a stretched child ratcheting — the panel writes a size and would
   * otherwise read it back as one the child asked for — and a measured axis cannot ratchet,
   * because the panel never reads a written size on it at all.
   */
  protected naturalSize(c: Control, available?: Size): { width: number; height: number } {
    const recorded = this.#natural.get(c) ?? { width: c.width, height: c.height };
    if (!c.autoWidth && !c.autoHeight) return recorded;
    const pref = c.preferredSize(available ?? this.clientSize);
    return {
      width: c.autoWidth ? pref.width : recorded.width,
      height: c.autoHeight ? pref.height : recorded.height,
    };
  }

  /**
   * Place one child across the main axis.
   *
   * `before`/`after` are the child's margin on this axis, held clear of the edges the alignment
   * measures from - a stretched child fills what is left of `available` after them, and the
   * others are positioned inside it. `align` defaults to the panel's own, so a child that says
   * nothing follows the panel and a child with its own `hAlign`/`vAlign` overrides it, exactly
   * as a `Grid` cell already worked.
   */
  protected crossPlace(
    available: number,
    size: number,
    before = 0,
    after = 0,
    align: Alignment = this.align,
  ): { offset: number; size: number } {
    const placed = alignAxis(align, available, size, before, after);
    return { offset: placed.offset, size: placed.extent };
  }

  /** The child's margin, split into the two axes this panel thinks in. */
  protected marginOf(c: Control): { mainBefore: number; mainAfter: number; crossBefore: number; crossAfter: number } {
    const m = c.margin;
    return this.horizontal
      ? { mainBefore: m.left, mainAfter: m.right, crossBefore: m.top, crossAfter: m.bottom }
      : { mainBefore: m.top, mainAfter: m.bottom, crossBefore: m.left, crossAfter: m.right };
  }

  /** The child's own cross-axis alignment, if it has one, else the panel's. */
  protected crossAlignOf(c: Control): Alignment {
    return (this.horizontal ? c.vAlign : c.hAlign) ?? this.align;
  }

  override onChildrenChanged(): void {
    this.performLayout();
    this.invalidate();
  }

  protected override onEnvironmentChanged(): void {
    // A theme change can resize children (fonts, metrics), so re-flow.
    this.performLayout();
  }

  /**
   * Anchors and layout panels both want to own child positions; the panel
   * wins. Anchoring a child inside one is meaningless.
   */
  protected override applyAnchors(): void {}

  /**
   * A layout panel places every child itself, alignment included - `GridPlacement.hAlign` and
   * the panel's own `align` are how a child says where it sits in the space it was given. The
   * container-level pass would be a second, competing answer.
   */
  protected override applyAlignment(): void {}

  #watch(): void {
    const current = this.controls.toArray();
    let unchanged = current.length === this.#watched.size;
    if (unchanged) {
      for (const c of current) {
        if (!this.#watched.has(c)) {
          unchanged = false;
          break;
        }
      }
    }
    if (unchanged) return;

    for (const [c, handler] of this.#watched) {
      c.off("resize", handler);
      c.off("visiblechanged", handler);
    }
    this.#watched.clear();

    for (const c of current) {
      // A resize outside a layout pass is the application's doing, so it
      // redefines what this child naturally wants.
      const handler = (): void => {
        if (this.#arranging) return;
        this.#natural.set(c, { width: c.width, height: c.height });
        this.performLayout();
      };
      c.on("resize", handler);
      c.on("visiblechanged", handler);
      this.#watched.set(c, handler);
      if (!this.#natural.has(c)) this.#natural.set(c, { width: c.width, height: c.height });
    }
  }
}
